import { useState } from 'react';
import { Send, Megaphone, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

export const AdminBroadcastForm = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [target, setTarget] = useState('all');
  const [sendEmail, setSendEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    if (!window.confirm(`Send this announcement to ${target.replace('_', ' ')}?`)) return;
    setSubmitting(true);
    try {
      const res = await api.post('/admin/notifications/broadcast', {
        title: title.trim(),
        body: body.trim(),
        link: link.trim() || null,
        target,
        send_email: sendEmail,
      });
      toast.success(`Broadcast sent to ${res.data?.recipient_count || 0} users`);
      setTitle('');
      setBody('');
      setLink('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-primary" />
          Broadcast Notification
        </CardTitle>
        <CardDescription>
          Send an in-app announcement (and optional email) to all users, campaign owners, or donors.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-broadcast-form">
          <div>
            <Label htmlFor="broadcast-title">Title</Label>
            <Input
              id="broadcast-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Scheduled maintenance tonight"
              maxLength={200}
              required
              data-testid="broadcast-title-input"
            />
          </div>
          <div>
            <Label htmlFor="broadcast-body">Message</Label>
            <Textarea
              id="broadcast-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your announcement..."
              rows={4}
              maxLength={2000}
              required
              data-testid="broadcast-body-input"
            />
            <p className="text-xs text-muted-foreground mt-1">{body.length}/2000</p>
          </div>
          <div>
            <Label htmlFor="broadcast-link">Link (optional)</Label>
            <Input
              id="broadcast-link"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="e.g. /discover or https://..."
              data-testid="broadcast-link-input"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="broadcast-target">Audience</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger id="broadcast-target" data-testid="broadcast-target-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All users</SelectItem>
                  <SelectItem value="campaign_owners">Campaign owners only</SelectItem>
                  <SelectItem value="donors">Donors only (verified)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={sendEmail}
                  onCheckedChange={setSendEmail}
                  id="broadcast-email"
                  data-testid="broadcast-send-email-checkbox"
                />
                <span className="text-sm">Also send by email</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full gap-2"
              data-testid="broadcast-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {submitting ? 'Sending...' : 'Send Broadcast'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
